/**
 * CLE-3015 — the Clear Certified certificate's design, in one place for both renderers: the on-screen
 * certificate (CertificateParts.tsx, also printed and shown on the public /certified/:credentialId page)
 * and the downloadable PDF (pages/CertificatePdf.tsx, @react-pdf/renderer). The PDF cannot read CSS
 * variables, so the colours are hex here; each one names the brand token it copies.
 */

/** What the certificate says; the page and the PDF render the same fields. */
export interface ICertificateContent {
  name: string;
  hospitalName: string;
  /** "October 21, 2026". */
  certifiedDate: string;
  curriculumVersion: number | null;
  /** The public credential id ("ABCD2345EFGH"), printed small; null when none is assigned yet. */
  credentialId?: string | null;
}

/**
 * Worded so it is also true for a user a SuperAdmin marked trained live (waived), who never took the
 * knowledge check: they completed the training, live.
 */
export const CERTIFICATE_BODY =
  "has completed the Clear Health platform training and is Clear Certified.";

export const CERTIFICATE_EYEBROW = "Certificate of completion";
export const CERTIFICATE_TITLE = "Clear Certified";
export const CERTIFICATE_CERTIFIES = "This certifies that";
export const DATE_LABEL = "Date certified";
export const CREDENTIAL_LABEL = "Credential ID";

export const curriculumVersionText = (version: number) =>
  `Curriculum version ${version}`;

/** The logo the app already ships, as SVG for the screen and as PNG for the PDF (it cannot embed SVG files). */
export const CERTIFICATE_LOGO_SVG = "/logo_new.svg";
export const CERTIFICATE_LOGO_PNG = "/logo_footer.png";

export const CERTIFICATE_COLORS = {
  /** --brand-black */
  navy: "#17143f",
  /** --brand-purple-700 */
  purple: "#4f46e5",
  /** --brand-purple-600 */
  purpleBright: "#5b4dfd",
  /** The logo mark's gradient: its first (teal) and middle (blue) stops. */
  teal: "#0be3c1",
  blue: "#3690e2",
  /** --brand-gray-600 */
  gray: "#706f7b",
  /** --brand-gray-400 */
  grayLight: "#a5a4ad",
  /** --brand-gray-200 */
  grayLine: "#e2e2e4",
  white: "#ffffff",
} as const;

/** A colour stop of a left-to-right / corner-to-corner gradient. */
export interface IGradientStop {
  offset: number;
  color: string;
}

export interface ICertificatePalette {
  /** The outer band, corner to corner: the logo mark's teal → blue, then brand purple → navy. */
  band: IGradientStop[];
  /** The thin rule under the name. */
  rule: IGradientStop[];
  title: string;
  seal: {
    rosette: IGradientStop[];
    ring: string;
    disc: string;
    hairline: string;
    check: string;
    tails: [string, string];
  };
}

const C = CERTIFICATE_COLORS;

export const CERTIFICATE_PALETTE: ICertificatePalette = {
  band: [
    { offset: 0, color: C.teal },
    { offset: 0.3, color: C.blue },
    { offset: 0.65, color: C.purple },
    { offset: 1, color: C.navy },
  ],
  rule: [
    { offset: 0, color: C.teal },
    { offset: 0.5, color: C.purpleBright },
    { offset: 1, color: C.purple },
  ],
  title: C.purple,
  seal: {
    rosette: [
      { offset: 0, color: C.teal },
      { offset: 0.45, color: C.blue },
      { offset: 1, color: C.purpleBright },
    ],
    ring: C.white,
    disc: C.purple,
    hairline: C.white,
    check: C.white,
    tails: [C.navy, C.purple],
  },
};

/**
 * A credential that is no longer current (public page only): the same certificate in grays, so it
 * cannot be mistaken for a live one even in a screenshot that crops the "Not current" note.
 */
export const CERTIFICATE_PALETTE_MUTED: ICertificatePalette = {
  band: [
    { offset: 0, color: C.grayLine },
    { offset: 1, color: C.grayLight },
  ],
  rule: [
    { offset: 0, color: C.grayLine },
    { offset: 1, color: C.grayLight },
  ],
  title: C.gray,
  seal: {
    rosette: [
      { offset: 0, color: C.grayLine },
      { offset: 1, color: C.grayLight },
    ],
    ring: C.white,
    disc: C.grayLight,
    hairline: C.white,
    check: C.white,
    tails: [C.gray, C.grayLight],
  },
};

export const cssGradient = (angle: string, stops: IGradientStop[]) =>
  `linear-gradient(${angle}, ${stops
    .map(({ offset, color }) => `${color} ${Math.round(offset * 100)}%`)
    .join(", ")})`;

/**
 * The "Clear Certified" seal. ONE slot for supplied artwork: when John sends a seal or ribbon image,
 * put the file in spa/public (PNG or JPG: the PDF cannot embed SVG files) and set its path here, e.g.
 * "/clear-certified-seal.png". Both the screen and the PDF then show the image, at SEAL_SIZE, in place
 * of the drawn seal below; nothing around it moves.
 */
export const SEAL_IMAGE_SRC: string | null = null;

/** The seal's box, in the drawing's units; the renderers scale it. Tall, for the ribbon tails. */
export const SEAL_VIEW_BOX = { width: 120, height: 150 };

/** A starburst: `points` tips on radius `outer`, alternating with notches on radius `inner`. */
export function starburstPoints(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  points: number
): string {
  const coords: string[] = [];
  for (let i = 0; i < points * 2; i += 1) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI * i) / points - Math.PI / 2;
    coords.push(
      `${(cx + radius * Math.cos(angle)).toFixed(2)},${(
        cy +
        radius * Math.sin(angle)
      ).toFixed(2)}`
    );
  }
  return coords.join(" ");
}

/** The drawn seal: two ribbon tails behind a 28-point rosette, a white ring, a purple disc, a check. */
export const SEAL_GEOMETRY = {
  center: { x: 60, y: 58 },
  rosette: starburstPoints(60, 58, 52, 46, 28),
  ringRadius: 40,
  discRadius: 35,
  hairlineRadius: 30,
  check: "M44 59 L55 70 L77 47",
  checkWidth: 7,
  tails: [
    "40,80 22,142 38,132 50,147 64,92",
    "80,80 98,142 82,132 70,147 56,92",
  ] as [string, string],
} as const;
